/* =============================================================================
   AOSA — testimonial pager

   Turns the testimonial cards into a paged deck: three at a time on desktop,
   two on tablet and phone, with prev/next, clickable dots and a count.
   Adding a tenth or a fiftieth testimonial changes nothing here — the page
   count grows, the section height does not.

   Why a pager and not an auto-playing carousel: the block this replaced was an
   auto-scrolling marquee, and moving text is why the testimonials were hard to
   read. Nothing here moves unless the reader asks it to.

   Progressive enhancement. Without JavaScript the CSS leaves the cards in a
   masonry column flow — longer, but complete and readable. The pager only
   appears once the script has built it.

   SEO / AEO: every testimonial stays in the HTML. Pages are hidden after the
   markup is served, so crawlers and answer engines still read all of them.

   To revert: remove the script tag in snippets/head-preload.liquid.
   ============================================================================= */
(function () {
  'use strict';

  var CARD = 'figure.rf-testimonial';

  /* Cards per page. Two on a phone rather than one: with nine testimonials,
     one per page produced nine dots, which reads as a chore rather than a
     control. Two stack vertically inside the page, so nothing is cramped, and
     the pager drops to five dots. */
  function perView(w) {
    if (w >= 1024) return 3;
    return 2;
  }

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }

  function init(root) {
    var track = root.querySelector('.rf-marquee-track');
    if (!track || root.dataset.aosaPager === 'on') return;

    var cards = Array.prototype.slice.call(track.querySelectorAll(CARD));
    if (cards.length < 2) return;

    var state = { index: 0, per: 0, pages: [] };

    var viewport = el('div', 'aosa-tp__viewport');
    var nav = el('nav', 'aosa-tp__nav', { 'aria-label': 'Testimonials' });
    var prev = el('button', 'aosa-tp__arrow aosa-tp__arrow--prev', { type: 'button', 'aria-label': 'Previous testimonials' });
    var next = el('button', 'aosa-tp__arrow aosa-tp__arrow--next', { type: 'button', 'aria-label': 'Next testimonials' });
    var dots = el('div', 'aosa-tp__dots');
    var count = el('p', 'aosa-tp__count');
    var live = el('p', 'aosa-tp__live', { 'aria-live': 'polite' });

    prev.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    next.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="9 18 15 12 9 6"></polyline></svg>';

    function paginate() {
      var per = perView(window.innerWidth);
      if (per === state.per) return false;
      state.per = per;

      state.pages.forEach(function (p) { p.remove(); });
      state.pages = [];

      for (var i = 0; i < cards.length; i += per) {
        var page = el('div', 'aosa-tp__page', { role: 'group', 'aria-label': 'Testimonials, page ' + (state.pages.length + 1) });
        cards.slice(i, i + per).forEach(function (c) { page.appendChild(c); });
        viewport.appendChild(page);
        state.pages.push(page);
      }

      dots.innerHTML = '';
      if (state.pages.length > 1) {
        state.pages.forEach(function (_, i) {
          var d = el('button', 'aosa-tp__dot', { type: 'button', 'aria-label': 'Go to page ' + (i + 1) + ' of ' + state.pages.length });
          d.addEventListener('click', function () { go(i); });
          dots.appendChild(d);
        });
      }

      if (state.index >= state.pages.length) state.index = state.pages.length - 1;
      return true;
    }

    function equalise() {
      viewport.style.minHeight = '';
      if (state.per < 2) return;
      var tallest = 0;
      state.pages.forEach(function (p) {
        var wasHidden = p.hasAttribute('hidden');
        if (wasHidden) { p.removeAttribute('hidden'); p.style.position = 'absolute'; p.style.visibility = 'hidden'; }
        tallest = Math.max(tallest, p.offsetHeight);
        if (wasHidden) { p.style.position = ''; p.style.visibility = ''; p.setAttribute('hidden', ''); }
      });
      if (tallest) viewport.style.minHeight = tallest + 'px';
    }

    function render(announce) {
      state.pages.forEach(function (p, i) {
        if (i === state.index) {
          p.removeAttribute('hidden');
          p.classList.add('is-current');
        } else {
          p.setAttribute('hidden', '');
          p.classList.remove('is-current');
        }
      });

      Array.prototype.forEach.call(dots.children, function (d, i) {
        if (i === state.index) { d.setAttribute('aria-current', 'true'); }
        else { d.removeAttribute('aria-current'); }
      });

      var total = state.pages.length;
      prev.disabled = state.index === 0;
      next.disabled = state.index === total - 1;
      count.textContent = (state.index + 1) + ' / ' + total;
      nav.hidden = total < 2;
      if (announce) live.textContent = 'Page ' + (state.index + 1) + ' of ' + total;
    }

    function go(i) {
      var total = state.pages.length;
      if (i < 0 || i > total - 1 || i === state.index) return;
      state.index = i;
      if (!reduced()) {
        viewport.classList.remove('is-swapping');
        void viewport.offsetWidth;
        viewport.classList.add('is-swapping');
      }
      render(true);
    }

    track.parentNode.insertBefore(viewport, track);
    nav.appendChild(prev);
    nav.appendChild(dots);
    nav.appendChild(next);
    nav.appendChild(count);
    viewport.parentNode.insertBefore(nav, viewport.nextSibling);
    viewport.parentNode.insertBefore(live, nav.nextSibling);
    track.remove();

    prev.addEventListener('click', function () { go(state.index - 1); });
    next.addEventListener('click', function () { go(state.index + 1); });

    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { go(state.index - 1); }
      else if (e.key === 'ArrowRight') { go(state.index + 1); }
    });

    var x0 = null;
    viewport.addEventListener('touchstart', function (e) { x0 = e.changedTouches[0].clientX; }, { passive: true });
    viewport.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 45) { go(state.index + (dx < 0 ? 1 : -1)); }
      x0 = null;
    }, { passive: true });

    paginate();
    render(false);
    root.dataset.aosaPager = 'on';
    requestAnimationFrame(equalise);
    window.addEventListener('load', equalise);

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        if (paginate()) render(false);
        equalise();
      }, 180);
    });
  }

  /* ---------------------------------------------------------------------------
     Reading order.

     The router asks "what are you here for?" and the hero answers it with the
     one-person-setup video. Proof belongs immediately after that answer, not
     three sections further down past the benefit grid and the comparison table.

     The markup order inside the 71KB hero section is fixed and not exposed to
     the theme editor, so the testimonials node is moved up here instead.

     A real DOM move, not CSS `order`: tab order, screen-reader reading order
     and the visual order stay in step. Crawlers read the served HTML, where all
     the testimonials are present either way, so nothing is hidden from them.

     Bails out unless the structure is exactly what it expects — hero and
     testimonials as siblings, testimonials currently below the hero.
     --------------------------------------------------------------------------- */
  function relocate(node) {
    var wrap = node.parentNode;
    if (!wrap || !wrap.children) return;

    var hero = null;
    for (var i = 0; i < wrap.children.length; i++) {
      if (wrap.children[i] === node) return; // already above the hero
      if (wrap.children[i].classList.contains('rf-hero')) { hero = wrap.children[i]; break; }
    }
    if (!hero || hero.nextElementSibling === node) return;

    wrap.insertBefore(node, hero.nextSibling);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.rf-testimonials'), function (node) {
      relocate(node);
      init(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
